import { useState } from "react";
import { tapFeedback, minusFeedback } from "../feedback";
import { signed } from "../format";
import { HoldButton } from "./HoldButton";
import { Emoji } from "./Emoji";
import { ALLOWED_DELTAS, MAX_SCORE, fitsCeiling } from "../../shared/score";
import { MAX_REASON_LENGTH, reasonsFor } from "../../shared/reasons";

/**
 * The grown-up controls: Add or Remove, a reason (a chip, or a few words typed in), then 1, 5
 * or 10. The amounts wait for a reason, as the server refuses a tap without one. Add fires on
 * tap; Remove needs a hold so a stray touch cannot take points away. An Add that would pass
 * 100 is greyed out too.
 */
export function TapPad({ score, onTap }: { score: number; onTap: (delta: number, reason: string | null) => void }) {
  const [sign, setSign] = useState<1 | -1>(1);
  const [chip, setChip] = useState<string | null>(null);
  const [typed, setTyped] = useState("");

  function chooseSign(next: 1 | -1) {
    tapFeedback();
    setSign(next);
    // Chips belong to one sign, so one picked for Add cannot ride along on a Remove. Typed
    // words stay: they were written for this tap, whichever way it goes.
    setChip(null);
  }

  function fire(size: number) {
    if (sign > 0) tapFeedback(25);
    else minusFeedback();
    if (!why) return;
    onTap(sign * size, why);
    setChip(null);
    setTyped("");
  }

  const room = MAX_SCORE - score;
  const why = chip ?? typed.trim();

  return (
    <section className="tap-pad" aria-label="Change points">
      <div className="segmented" role="tablist" aria-label="Add or remove">
        <button
          type="button"
          role="tab"
          aria-selected={sign > 0}
          className="segmented__option"
          onClick={() => chooseSign(1)}
        >
          <Emoji>➕</Emoji>Add
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={sign < 0}
          className="segmented__option"
          onClick={() => chooseSign(-1)}
        >
          <Emoji>➖</Emoji>Remove
        </button>
      </div>

      <div className="reasons" role="group" aria-label="Reason">
        {reasonsFor(sign).map((r) => (
          <button
            key={r}
            type="button"
            className="reason-chip"
            aria-pressed={chip === r}
            onClick={() => {
              tapFeedback();
              setChip(chip === r ? null : r);
              setTyped("");
            }}
          >
            {r}
          </button>
        ))}
      </div>

      <label className="field">
        <span className="visually-hidden">Or type a reason</span>
        <input
          className="input reason-input"
          type="text"
          placeholder="Or type a reason"
          autoComplete="off"
          enterKeyHint="done"
          maxLength={MAX_REASON_LENGTH}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            if (e.target.value) setChip(null);
          }}
        />
      </label>

      {!why && <p className="hint tap-pad__hint">Pick or type a reason first.</p>}
      <div className="amounts">
        {ALLOWED_DELTAS.map((size) =>
          sign > 0 ? (
            <button
              key={size}
              type="button"
              className="amount"
              aria-label={`Add ${size}`}
              disabled={!why || !fitsCeiling(score, size)}
              onClick={() => fire(size)}
            >
              {signed(size)}
            </button>
          ) : (
            <HoldButton
              key={size}
              className="amount"
              label={`Hold to remove ${size}`}
              disabled={!why}
              onHoldStart={() => tapFeedback()}
              onConfirm={() => fire(size)}
            >
              {signed(-size)}
            </HoldButton>
          ),
        )}
      </div>
      {why && sign < 0 && <p className="hint tap-pad__hint">Press and hold to remove points.</p>}
      {why && sign > 0 && room < ALLOWED_DELTAS[ALLOWED_DELTAS.length - 1] && (
        <p className="hint tap-pad__hint">
          {room === 0 ? "Already at 100, the most there is." : `Up to ${room} more to reach 100.`}
        </p>
      )}
    </section>
  );
}
